import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, ArrowUpRight, Building2, Mail, MapPin, Phone, Store } from 'lucide-react';
import { motion } from 'framer-motion';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { useI18n } from '../../context/I18nContext';
import AnimatedEyebrow from '../AnimatedEyebrow';

const showroomIcons = [Building2, Store, MapPin];

const phoneMapping = {
  'Nabeul Store': '+216 29 36 30 52',
  'Tunis Showroom': '+216 29 36 30 55',
  'Showroom Tunis': '+216 29 36 30 55',
  'Sousse Showroom': '+216 26 66 00 66',
  'Showroom Sousse': '+216 26 66 00 66',
};

const showroomNameAliases = {
  'CDS Nabeul': 'Nabeul Store',
  'Nabeul Showroom': 'Nabeul Store',
  'CDS Tunis': 'Tunis Showroom',
  'Tunis Store': 'Tunis Showroom',
  'Showroom TUNIS': 'Tunis Showroom',
  'TUNIS Showroom': 'Tunis Showroom',
  'CDS Sousse': 'Sousse Showroom',
  'Sousse Store': 'Sousse Showroom',
  'Showroom Sousse': 'Sousse Showroom',
};

const showroomTitleKeyMapping = {
  'Nabeul Store': 'nabeul',
  'Tunis Showroom': 'tunis',
  'Sousse Showroom': 'sousse',
};

const defaultShowrooms = [
  { _id: 'fallback-nabeul', name: 'Nabeul Store', address: '' },
  { _id: 'fallback-tunis', name: 'Tunis Showroom', address: '' },
  { _id: 'fallback-sousse', name: 'Sousse Showroom', address: '' },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.08,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 26 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.72, ease: [0.22, 1, 0.36, 1] },
  },
};

function splitAddressIntoTwoLines(address = '') {
  const value = String(address || '').trim();
  if (!value) {
    return ['', ''];
  }

  const commaIndex = value.indexOf(',');
  if (commaIndex > 0 && commaIndex < value.length - 1) {
    return [value.slice(0, commaIndex + 1).trim(), value.slice(commaIndex + 1).trim()];
  }

  const words = value.split(/\s+/).filter(Boolean);
  if (words.length <= 1) {
    return [value, ''];
  }

  const splitIndex = Math.ceil(words.length / 2);
  return [words.slice(0, splitIndex).join(' '), words.slice(splitIndex).join(' ')];
}

function buildMapLinkHref(name = '', address = '') {
  const query = [name, address].filter(Boolean).join(', ');
  if (!query) {
    return '';
  }

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

export default function ContactChannelsSection() {
  const { language, t } = useI18n();
  const [showrooms, setShowrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const previousButtonRef = useRef(null);
  const nextButtonRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    async function loadShowrooms() {
      try {
        const res = await fetch('/api/showrooms');
        const data = await res.json().catch(() => []);
        if (!isMounted) return;
        const rooms = Array.isArray(data) ? data : [];
        
        const normalized = rooms.map(room => {
          const rawName = String(room?.name || '').trim();
          const normalizedName = showroomNameAliases[rawName] || rawName;
          return { ...room, name: normalizedName };
        });
        
        // Sort to put Nabeul Store first
        normalized.sort((a, b) => {
          if (a.name === 'Nabeul Store') return -1;
          if (b.name === 'Nabeul Store') return 1;
          return 0;
        });
        
        setShowrooms(normalized.length ? normalized : defaultShowrooms);
      } catch (error) {
        if (!isMounted) return;
        setShowrooms(defaultShowrooms);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadShowrooms();

    return () => {
      isMounted = false;
    };
  }, []);

  const showroomItems = loading
    ? Array.from({ length: 3 }).map((_, index) => ({ _id: `loading-${index}`, loading: true }))
    : showrooms;
  const hasNavigation = !loading && showroomItems.length > 3;

  return (
    <section className="relative overflow-hidden border-t border-white/5 bg-[var(--dash-main)] py-24 text-white">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 opacity-80 bg-[url('/bg/2.webp')] bg-no-repeat bg-[size:100%_100%]" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(255,255,255,0.14)_0%,transparent_34%),radial-gradient(circle_at_85%_10%,rgba(255,255,255,0.08)_0%,transparent_30%),radial-gradient(circle_at_50%_85%,rgba(255,255,255,0.06)_0%,transparent_36%)]" />
      </div>

      <div className="relative z-10 container mx-auto px-6 lg:px-10">
        <motion.div
          className="mb-16 grid gap-8 md:grid-cols-12 md:items-end"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-120px' }}
        >
          <motion.div className="md:col-span-8 max-w-2xl" variants={itemVariants}>
            <AnimatedEyebrow>{t('contactPage.channels.eyebrow')}</AnimatedEyebrow>
            <motion.h2 variants={itemVariants} className="mt-4 text-4xl font-light uppercase leading-tight text-white sm:text-5xl">
              {t('contactPage.channels.titleLine1')}{' '}
              <span className="block font-black text-white">{t('contactPage.channels.titleLine2')}</span>
            </motion.h2>
            <motion.p variants={itemVariants} className="mt-5 max-w-2xl text-sm leading-7 text-white/85 sm:text-base">
              {t('contactPage.channels.description')}
            </motion.p>
          </motion.div>
          <motion.div className="md:col-span-4 md:justify-self-end" variants={itemVariants}>
            <div className="flex flex-wrap items-center justify-end gap-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.22em] text-white">
                <span className="h-2 w-2 rounded-full bg-white" />
                {showrooms.length || 3} locations, one team
              </div>
              {hasNavigation ? (
                <div className="flex items-center gap-2">
                  <button
                    ref={previousButtonRef}
                    type="button"
                    aria-label="Previous showrooms"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-white/10 text-white transition-colors hover:bg-white hover:text-[var(--dash-black)]"
                  >
                    <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button
                    ref={nextButtonRef}
                    type="button"
                    aria-label="Next showrooms"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-white/10 text-white transition-colors hover:bg-white hover:text-[var(--dash-black)]"
                  >
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              ) : null}
            </div>
          </motion.div>
        </motion.div>

        <Swiper
          key={hasNavigation ? 'showrooms-with-navigation' : 'showrooms-static'}
          className="contact-showrooms-swiper"
          modules={hasNavigation ? [Navigation] : []}
          slidesPerView={1}
          spaceBetween={20}
          watchOverflow
          breakpoints={{
            640: { slidesPerView: 2, spaceBetween: 24 },
            1280: { slidesPerView: 3, spaceBetween: 32 },
          }}
          navigation={hasNavigation}
          onSwiper={(swiper) => {
            if (!hasNavigation || !swiper.navigation || !previousButtonRef.current || !nextButtonRef.current) return;
            swiper.params.navigation.prevEl = previousButtonRef.current;
            swiper.params.navigation.nextEl = nextButtonRef.current;
            swiper.navigation.destroy();
            swiper.navigation.init();
            swiper.navigation.update();
          }}
        >
          {showroomItems.map((showroom, index) => {
            const Icon = showroomIcons[index % showroomIcons.length];
            const translatedName = showroom?.translations?.[language]?.name || '';
            const translatedAddress = showroom?.translations?.[language]?.address || '';
            const showroomName = translatedName || showroom?.name || t('admin.showrooms.unnamed', 'Unnamed');
            const canonicalName = showroom?.name || showroomName;
            const showroomTitleKey = translatedName ? null : showroomTitleKeyMapping[canonicalName];
            const showroomDisplayName = showroomTitleKey
              ? t(`contactPage.channels.showroomTitles.${showroomTitleKey}`, showroomName)
              : showroomName;
            const showroomAddress = translatedAddress || showroom?.address || '';
            const mappedPhone = phoneMapping[canonicalName] || phoneMapping[showroomName] || showroom?.phone || '';
            const showroomEmail = 'sales@etc.tn';
            const showroomKey = String(showroom?._id || showroomName || index);
            const [addressLineOne, addressLineTwo] = splitAddressIntoTwoLines(showroomAddress);
            const mapHref = buildMapLinkHref(canonicalName, showroomAddress);

            return (
              <SwiperSlide key={showroom?._id || showroomName || index} className="h-auto">
                <motion.article
                  variants={itemVariants}
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="group h-full overflow-hidden rounded-[26px] border border-white/10 bg-[var(--dash-black)] transition-all duration-300 hover:-translate-y-1 hover:border-[var(--dash-main)]/40"
                >
                {/* <div className="relative h-52 overflow-hidden border-b border-white/10 bg-[#111] sm:h-56">
                  {loading ? (
                    <div className="animate-pulse absolute inset-0 bg-white/5" />
                  ) : (
                    <a
                      href={mapHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative h-full w-full block overflow-hidden"
                    >
                      <img
                        src="/map.webp"
                        alt={`${showroomDisplayName} location`}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/20 transition-all duration-300 group-hover:bg-black/10" />
                    </a>
                  )}
                </div> */}

                <div className="p-7 sm:p-8">
                  <div className="mb-6 flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-white backdrop-blur-sm transition-colors duration-300 group-hover:bg-[var(--dash-main)] group-hover:text-[var(--dash-black)]">
                        <Icon className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="mt-1 text-2xl font-black leading-snug text-white">
                          {loading ? t('contactPage.channels.loading', 'Loading...') : showroomDisplayName}
                        </h3>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 border-t border-white/10 pt-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[var(--dash-main)]" />
                      {loading ? (
                        <p className="text-sm leading-relaxed text-white/70">
                          {t('contactPage.channels.items.2.hint')}
                        </p>
                      ) : showroomAddress ? (
                        <a
                          href={mapHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group inline-flex flex-col text-sm leading-relaxed text-white/70 transition-colors hover:text-white"
                        >
                          <span className="block">{addressLineOne}</span>
                          {addressLineTwo ? <span className="block">{addressLineTwo}</span> : null}
                          <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.22em] text-white/35 opacity-90 transition-[color,opacity] duration-200 group-hover:opacity-100 group-hover:text-[var(--dash-main)]">
                            {t('contactPage.channels.openMap', 'Open map')}
                            <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
                          </span>
                        </a>
                      ) : (
                        <p className="text-sm leading-relaxed text-white/45">-</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 shrink-0 text-[var(--dash-main)]" />
                      {mappedPhone ? (
                        <a
                          href={`tel:${mappedPhone.replace(/\s+/g, '')}`}
                          className="text-sm font-medium text-white/70 transition-colors hover:text-white"
                        >
                          {mappedPhone}
                        </a>
                      ) : (
                        <span className="text-sm text-white/45">-</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 shrink-0 text-[var(--dash-main)]" />
                      <a
                        href="tel:+21626363025"
                        className="text-sm font-medium text-white/70 transition-colors hover:text-white"
                      >
                        +216 26 36 30 25
                      </a>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 shrink-0 text-[var(--dash-main)]" />
                      {showroomEmail ? (
                        <a
                          href={`mailto:${showroomEmail}`}
                          className="text-sm font-medium text-white/70 transition-colors hover:text-white"
                        >
                          {showroomEmail}
                        </a>
                      ) : (
                        <span className="text-sm text-white/45">-</span>
                      )}
                    </div>
                  </div>
                </div>
                </motion.article>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </section>
  );
}
